import { createContext, useCallback, useContext, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useExchangeRates } from '@/api/hooks/useCurrency';
import { meQueryKey, useMe } from '@/api/hooks/useAuth';
import { authService } from '@/api/services/auth';
import type { UserResponse } from '@/api/types';
import { formatCurrency, setActiveNumberLocale } from '@/lib/format';

/** Default display currency when the user has no preference yet (Europe-first).
 *  Prices are sourced in USD and converted to this currency for display. */
export const DEFAULT_CURRENCY = 'EUR';

/** Sentinel meaning "use the browser/OS locale" for number formatting. */
export const AUTO_LOCALE = 'auto';

/** Currencies pinned to the top of the picker. The rest of the list is whatever
 *  the live-rate provider returns, so the picker grows automatically. */
export const PINNED_CURRENCIES = ['USD', 'EUR', 'GBP', 'DKK', 'SEK', 'CHF'] as const;

/** Number-format choices offered in Settings. `value` is what we persist;
 *  'auto' resolves to the browser locale at render time. */
export const NUMBER_LOCALE_OPTIONS = [
  { value: AUTO_LOCALE, locale: undefined },
  { value: 'en-US', locale: 'en-US' }, // 1,234.56
  { value: 'de-DE', locale: 'de-DE' }, // 1.234,56
  { value: 'fr-FR', locale: 'fr-FR' }, // 1 234,56
] as const;

function resolveLocale(pref: string): string {
  if (pref && pref !== AUTO_LOCALE) return pref;
  if (typeof navigator !== 'undefined' && navigator.language) return navigator.language;
  return 'en-US';
}

type PreferencesPatch = { display_currency?: string; number_locale?: string };

type CurrencyContextValue = {
  /** The user's chosen ISO-4217 display currency (falls back to EUR). */
  currency: string;
  /** USD-base rates: price_in_currency = usd * rates[currency]. */
  rates: Record<string, number>;
  /** True when the rates are a fallback table (live provider unreachable). */
  ratesStale: boolean;
  /** The raw number-format preference ('auto' or a BCP-47 tag). */
  numberLocalePref: string;
  /** The resolved locale actually used for formatting ('auto' -> browser). */
  locale: string;
  /** The currency amounts are actually shown in: `currency`, or USD while
   *  there is no rate for it (an unconverted amount is never relabelled). */
  displayCode: string;
  /** Convert a USD amount to the display currency (identity if no rate / USD). */
  convert: (usd: number | null | undefined) => number | null | undefined;
  /** Convert an amount the user typed in `displayCode` back to USD. */
  toUsd: (amount: number | null | undefined) => number | null | undefined;
  /** Convert + format a USD amount for display in the chosen currency. */
  formatPrice: (usd: number | null | undefined) => string;
  /** Compact and whole-unit (e.g. "€4.9K", "€430"): only for monthly totals and
   *  budget brackets. An hourly or sub-unit price needs `formatPrice`, since
   *  this would round it to "€0". */
  formatPriceCompact: (usd: number) => string;
  /** Convert + format an amount quoted in `source` (ISO-4217, default USD) for
   *  display in the chosen currency. Without a rate for `source` the amount is
   *  shown in `source`, never relabelled. */
  formatAmount: (amount: number | null | undefined, source?: string | null) => string;
  /** Persist a new display currency to the user's profile. */
  setCurrency: (code: string) => Promise<void>;
  /** Persist a new number-format preference to the user's profile. */
  setNumberLocale: (pref: string) => Promise<void>;
  isUpdating: boolean;
};

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { data: ratesData } = useExchangeRates();

  const currency = me?.display_currency || DEFAULT_CURRENCY;
  const rates = ratesData?.rates ?? {};
  const ratesStale = ratesData?.stale ?? false;

  const numberLocalePref = me?.number_locale || AUTO_LOCALE;
  const locale = resolveLocale(numberLocalePref);
  // Make every number/currency formatter in `format.ts` use this locale, so
  // numbers and money stay consistent. Set during render (before children
  // render) and idempotent, so consumers pick it up on the same commit.
  setActiveNumberLocale(locale);

  const mutation = useMutation({
    mutationFn: (patch: PreferencesPatch) => authService.updatePreferences(patch),
    onSuccess: (updated: UserResponse) => {
      // Reflect the change immediately, then revalidate from the server.
      qc.setQueryData(meQueryKey, updated);
      qc.invalidateQueries({ queryKey: meQueryKey });
    },
  });

  const convert = useCallback(
    (usd: number | null | undefined) => {
      if (usd === null || usd === undefined || Number.isNaN(usd)) return usd;
      const rate = rates[currency];
      if (currency === 'USD' || !rate) return usd;
      return usd * rate;
    },
    [rates, currency],
  );

  const displayCode = currency !== 'USD' && rates[currency] ? currency : 'USD';

  const toUsd = useCallback(
    (amount: number | null | undefined) => {
      if (amount === null || amount === undefined || Number.isNaN(amount)) return amount;
      const rate = rates[currency];
      if (currency === 'USD' || !rate) return amount;
      return amount / rate;
    },
    [rates, currency],
  );

  const formatPrice = useCallback(
    (usd: number | null | undefined) => {
      if (usd === null || usd === undefined || Number.isNaN(usd)) return formatCurrency(usd);
      const rate = rates[currency];
      // No usable rate (or already USD) → render the raw USD figure as USD so we
      // never label an unconverted amount with the wrong currency symbol.
      if (currency === 'USD' || !rate) return formatCurrency(usd, 'USD');
      return formatCurrency(usd * rate, currency);
    },
    [rates, currency],
  );

  const formatPriceCompact = useCallback(
    (usd: number) => {
      const rate = rates[currency];
      const value = currency === 'USD' || !rate ? usd : usd * rate;
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: displayCode,
        notation: value >= 1000 ? 'compact' : 'standard',
        maximumFractionDigits: value >= 1000 ? 1 : 0,
      }).format(value);
    },
    [rates, currency, locale, displayCode],
  );

  const formatAmount = useCallback(
    (amount: number | null | undefined, source?: string | null) => {
      const from = (source || 'USD').toUpperCase();
      if (amount === null || amount === undefined || Number.isNaN(amount)) {
        return formatCurrency(amount);
      }
      if (from === 'USD') return formatPrice(amount);
      if (from === currency) return formatCurrency(amount, from);
      const fromRate = rates[from];
      if (!fromRate) return formatCurrency(amount, from);
      return formatPrice(amount / fromRate);
    },
    [rates, currency, formatPrice],
  );

  const setCurrency = useCallback(
    async (code: string) => {
      await mutation.mutateAsync({ display_currency: code });
    },
    [mutation],
  );

  const setNumberLocale = useCallback(
    async (pref: string) => {
      await mutation.mutateAsync({ number_locale: pref });
    },
    [mutation],
  );

  const value = useMemo<CurrencyContextValue>(
    () => ({
      currency,
      rates,
      ratesStale,
      numberLocalePref,
      locale,
      displayCode,
      convert,
      toUsd,
      formatPrice,
      formatPriceCompact,
      formatAmount,
      setCurrency,
      setNumberLocale,
      isUpdating: mutation.isPending,
    }),
    [
      currency,
      rates,
      ratesStale,
      numberLocalePref,
      locale,
      displayCode,
      convert,
      toUsd,
      formatPrice,
      formatPriceCompact,
      formatAmount,
      setCurrency,
      setNumberLocale,
      mutation.isPending,
    ],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
}

/** Ergonomic alias for the most common need: a USD→display-currency formatter. */
export function usePrice() {
  return useCurrency().formatPrice;
}
